<template>
  <div class="d-flex flex-column flex-root">
    <div class="d-flex flex-column flex-lg-row flex-column-fluid">
      <div class="d-flex flex-column flex-lg-row-fluid py-10">
        <div class="d-flex flex-center flex-column flex-column-fluid">
          <div class="card w-lg-750px two-factor-enable shadow-sm">
            <div class="card-body p-10">
              <div class="text-center mb-8">
                <span class="two-factor-setup__hero-icon mx-auto mb-5">
                  <i class="bi bi-shield-lock"></i>
                </span>
                <h1 class="text-dark fw-bolder mb-2">Ativar autenticação em dois fatores</h1>
                <p class="text-muted mb-0">
                  Sua conta exige verificação extra. Configure o autenticador para continuar.
                </p>
              </div>

              <div class="two-factor-enable__steps mb-8">
                <div class="two-factor-enable__step" :class="{ 'two-factor-enable__step--active': setupStep >= 1, 'two-factor-enable__step--done': setupStep > 1 }">
                  <span class="two-factor-enable__step-number">1</span>
                  <span class="two-factor-enable__step-label">Escanear</span>
                </div>
                <div class="two-factor-enable__step-line" :class="{ 'two-factor-enable__step-line--active': setupStep >= 2 }"></div>
                <div class="two-factor-enable__step" :class="{ 'two-factor-enable__step--active': setupStep >= 2, 'two-factor-enable__step--done': setupStep > 2 }">
                  <span class="two-factor-enable__step-number">2</span>
                  <span class="two-factor-enable__step-label">Confirmar</span>
                </div>
                <div class="two-factor-enable__step-line" :class="{ 'two-factor-enable__step-line--active': setupStep >= 3 }"></div>
                <div class="two-factor-enable__step" :class="{ 'two-factor-enable__step--active': setupStep >= 3 }">
                  <span class="two-factor-enable__step-number">3</span>
                  <span class="two-factor-enable__step-label">Recuperação</span>
                </div>
              </div>

              <div v-if="setupStep === 0" class="two-factor-enable__loading text-center py-10">
                <span class="spinner-border text-primary mb-4" v-if="!statusMessage"></span>
                <p class="text-gray-800 fw-semibold mb-1">
                  {{ statusMessage || 'Preparando QR Code' }}
                </p>
                <p class="text-muted fs-7 mb-0" v-if="!statusMessage">
                  Aguarde enquanto geramos sua chave de autenticação...
                </p>
              </div>

              <template v-else-if="setupStep === 2">
                <div class="row g-8 align-items-center">
                  <div class="col-md-6">
                    <div class="two-factor-enable__instruction mb-5">
                      <span class="two-factor-enable__instruction-icon">
                        <i class="bi bi-qr-code-scan"></i>
                      </span>
                      <div>
                        <p class="fw-bold text-gray-900 mb-1">Abra seu autenticador</p>
                        <p class="text-muted fs-7 mb-0">
                          Escaneie o QR code ao lado ou adicione a conta manualmente no app.
                        </p>
                      </div>
                    </div>

                    <div class="two-factor-enable__apps d-flex flex-wrap gap-2 mb-6">
                      <span class="two-factor-enable__app-badge">
                        <i class="bi bi-google me-1"></i> Google Authenticator
                      </span>
                      <span class="two-factor-enable__app-badge">
                        <i class="bi bi-microsoft me-1"></i> Microsoft Authenticator
                      </span>
                    </div>

                    <form @submit.prevent="confirmSetup">
                      <label class="form-label fw-semibold required">Código de verificação</label>
                      <input
                        v-model="confirmCode"
                        class="form-control form-control-lg form-control-solid two-factor-enable__code-input text-center mb-3"
                        maxlength="64"
                        autocomplete="one-time-code"
                        inputmode="numeric"
                        placeholder="000000"
                        required
                      />
                      <div class="form-text mb-5">Informe o código de 6 dígitos exibido no aplicativo.</div>
                      <button type="submit" class="btn btn-primary w-100" :disabled="busy || !confirmCode.trim()">
                        <span v-if="!busy">Confirmar e continuar</span>
                        <span v-else>
                          Confirmando...
                          <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      </button>
                    </form>
                  </div>

                  <div class="col-md-6">
                    <div class="two-factor-enable__qr-card text-center">
                      <span class="text-muted fs-8 fw-semibold text-uppercase d-block mb-4">QR Code</span>
                      <div class="two-factor-enable__qr-frame mx-auto" v-html="qrSvg"></div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else-if="setupStep === 3">
                <div class="alert alert-warning d-flex align-items-start gap-3 mb-6">
                  <i class="bi bi-exclamation-triangle fs-2 text-warning"></i>
                  <div>
                    <p class="fw-bold mb-1">Guarde estes códigos agora</p>
                    <p class="mb-0 fs-7">
                      Cada código pode ser usado uma única vez caso você perca acesso ao autenticador.
                      Salve em local seguro — eles não serão exibidos novamente.
                    </p>
                  </div>
                </div>

                <div class="two-factor-enable__codes-grid mb-6">
                  <div
                    v-for="(code, index) in confirmedRecoveryCodes"
                    :key="index"
                    class="two-factor-enable__code-chip"
                  >
                    {{ code }}
                  </div>
                </div>

                <div class="d-flex flex-wrap gap-3">
                  <button type="button" class="btn btn-light-primary" @click="downloadCodes(confirmedRecoveryCodes)">
                    <i class="bi bi-download me-1"></i>
                    Baixar códigos
                  </button>
                  <button type="button" class="btn btn-primary" @click="finishSetup">
                    Continuar para o sistema
                  </button>
                </div>
              </template>

              <div v-if="setupStep !== 3" class="text-center mt-8 pt-6 border-top">
                <button
                  type="button"
                  class="btn btn-link text-muted fw-semibold"
                  :disabled="busy"
                  @click="logout"
                >
                  Sair e entrar com outra conta
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Alert from '../../helpers/alert';
import { TwoFactorApi, applyAuthResponse, downloadRecoveryCodes } from '../../helpers/twoFactor';
import { initWebSocket } from '../../websocket';

export default {
  name: 'SetupTwoFactor',
  data() {
    return {
      qrSvg: null,
      recoveryCodes: [],
      confirmedRecoveryCodes: [],
      confirmCode: '',
      busy: false,
      statusMessage: null,
    };
  },
  computed: {
    setupStep() {
      if (this.confirmedRecoveryCodes.length) {
        return 3;
      }

      if (this.qrSvg) {
        return 2;
      }

      return 0;
    },
  },
  created() {
    if (!this.$store.getters['auth/isAuth']) {
      this.$router.replace('/login');
      return;
    }

    this.startSetup();
  },
  methods: {
    downloadCodes: downloadRecoveryCodes,
    startSetup() {
      this.busy = true;
      TwoFactorApi.enable()
        .then((response) => {
          this.qrSvg = response.data.qr_code_svg;
          this.recoveryCodes = response.data.recovery_codes ?? [];
        })
        .catch((error) => {
          this.statusMessage = error.response?.data?.message ?? 'Não foi possível iniciar o 2FA.';
        })
        .finally(() => {
          this.busy = false;
        });
    },
    confirmSetup() {
      this.busy = true;
      TwoFactorApi.confirm(this.confirmCode.trim())
        .then((confirmResponse) => {
          this.recoveryCodes = confirmResponse.data.recovery_codes ?? this.recoveryCodes;
          return TwoFactorApi.exchangeToken();
        })
        .then((response) => {
          applyAuthResponse(this.$store, response);
          initWebSocket();
          this.confirmedRecoveryCodes = this.recoveryCodes;
          Alert.toastSuccess('Autenticação em dois fatores configurada.');
        })
        .catch((error) => {
          const message = error.response?.data?.errors?.code?.[0]
            ?? error.response?.data?.message
            ?? 'Confirmação falhou.';
          Alert.toastError(message);
        })
        .finally(() => {
          this.busy = false;
        });
    },
    finishSetup() {
      this.$router.replace('/places');
    },
    logout() {
      this.$store.dispatch('auth/logout').then(() => {
        window.location.replace('/login');
      });
    },
  },
};
</script>

<style scoped>
.two-factor-setup__hero-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  background: #f1faff;
  color: #009ef7;
  font-size: 1.75rem;
}

.two-factor-enable__steps {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.two-factor-enable__step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #a1a5b7;
  min-width: 0;
}

.two-factor-enable__step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: #f5f8fa;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.two-factor-enable__step-label {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.two-factor-enable__step--active {
  color: #181c32;
}

.two-factor-enable__step--active .two-factor-enable__step-number,
.two-factor-enable__step--done .two-factor-enable__step-number {
  background: #e8fff3;
  color: #50cd89;
}

.two-factor-enable__step-line {
  flex: 1;
  height: 2px;
  background: #eff2f5;
  min-width: 1rem;
}

.two-factor-enable__step-line--active {
  background: #50cd89;
}

.two-factor-enable__instruction {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.two-factor-enable__instruction-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: #f1faff;
  color: #009ef7;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.two-factor-enable__app-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  border-radius: 0.5rem;
  background: #f9f9f9;
  border: 1px solid #eff2f5;
  color: #5e6278;
  font-size: 0.8rem;
  font-weight: 600;
}

.two-factor-enable__code-input {
  letter-spacing: 0.35em;
  font-size: 1.35rem;
  font-weight: 700;
}

.two-factor-enable__qr-card {
  background: #fafafa;
  border: 1px dashed #e4e6ef;
  border-radius: 1rem;
  padding: 1.5rem;
}

.two-factor-enable__qr-frame {
  display: inline-flex;
  padding: 1rem;
  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.04);
}

.two-factor-enable__qr-frame :deep(svg) {
  width: 180px !important;
  height: 180px !important;
}

.two-factor-enable__codes-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.two-factor-enable__code-chip {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: #f9f9f9;
  border: 1px solid #eff2f5;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9rem;
  font-weight: 600;
  color: #181c32;
  text-align: center;
}

@media (max-width: 767.98px) {
  .two-factor-enable__step-label {
    display: none;
  }

  .two-factor-enable__codes-grid {
    grid-template-columns: 1fr;
  }
}
</style>
